import React from "react";
import { Box, Flex, Heading, Text } from "rebass";
import {
  maxWidth,
  borderTop,
  space,
  themeGet,
  height,
  display
} from "styled-system";
import styled from "styled-components";
import AddToCalendar from "react-add-to-calendar";
import { Breadcrumbs } from "../components/breadcrumbs";
import { Carousel } from "../components/carousel";
import { Button } from "../components/button";
import { ModuleGrid } from "../components/module-grid";
import { SocialShare } from "../components/social-share";
import { WYSIWYG } from "../components/wysiwyg";
import { LazyImage } from "../components/lazy-image";
import { DetailEmbed } from "../components/detail-embed";
import { RelatedEvents } from "../components/related-events";
import { theme } from "../styles/theme";
import t from "typy";
import { ResponsiveIframe } from "../components/responsive-iframe";
import { Tags } from "./tags";
import { DynamicNextLink } from "../components/dynamic-next-link";

const DetailLayout = ({
  breadcrumbLinks,
  facebook_video_url,
  scheduled_date,
  start_date,
  end_date,
  timelineYear,
  title,
  details,
  location,
  address,
  directions_url,
  video,
  rsvp,
  image,
  fullImage = false,
  description,
  image_carousel,
  related,
  sidebarRelated,
  excerpt,
  showExcerpt = false,
  tags,
  handleDangerously = false,
  whiteText = false
}) => {
  let rsvpLink;

  if (rsvp && rsvp.url) {
    rsvpLink = {
      internal: false,
      href: rsvp.url,
      as: rsvp.url
    };
  }

  let directionsLink;

  if (directions_url && directions_url.url) {
    directionsLink = {
      internal: false,
      href: directions_url.url,
      as: directions_url.url
    };
  }

  let calendarEvent;

  if (start_date && !handleDangerously) {
    calendarEvent = {
      title: title,
      description: excerpt,
      location: location,
      startTime: start_date,
      endTime: end_date
    };
  }

  const hasFacebookVideo = facebook_video_url && facebook_video_url.length > 0;
  const hasYTVideo = t(video, "html").isDefined;
  const hasVideo = hasFacebookVideo || hasYTVideo;
  const hasImage = t(image, "url").isDefined;
  const noMedia = !hasImage && !hasFacebookVideo && !video;
  const fullMedia = !noMedia && (fullImage || hasFacebookVideo || hasVideo);
  const halfImage = !fullMedia && hasImage;
  const hasExcerpt = showExcerpt && t(excerpt, "length").isDefined;
  const hasRelated =
    t(related, "items.length").isDefined && related.items.length > 0;
  let pastEvent;
  if (end_date || start_date) {
    const today = new Date().toJSON().slice(0, 10);
    const eventDate = end_date
      ? end_date.slice(0, 10)
      : start_date.slice(0, 10);
    pastEvent = eventDate < today;
  } else {
    pastEvent = null;
  }

  return (
    <Box pt={[3, 3, 3, 7]}>
      <BreadcrumbsWrap px={[3, 3, 3, 4]} maxWidth={3} mx="auto">
        <Breadcrumbs crumbs={breadcrumbLinks} />
      </BreadcrumbsWrap>
      <Box
        bg={whiteText ? "transparent" : "white"}
        mt={noMedia ? [3] : [5, 5, 92]}
        px={[3, 3, 3, 4]}
        pb={[3, 3, 5]}
      >
        {fullMedia && (
          <Container maxWidth={3} mx="auto">
            {/* FB video takes priority */}
            {hasFacebookVideo && (
              <EmbedChild
                mt={[-35, -35, -65]}
                fbUrl={facebook_video_url}
                scheduled_date={scheduled_date}
              />
            )}
            {hasYTVideo && (
              <Box width={1} mt={[-35, -35, -65]}>
                <ResponsiveIframe iframe={video.html} />
              </Box>
            )}
            {!hasVideo && (
              <ImageWrap
                mt={[-35, -35, -65]}
                width={1}
                height={[200, 400, 600]}
              >
                <LazyImage
                  src={image.url}
                  alt={image.alt}
                  singleRender={false}
                />
              </ImageWrap>
            )}
          </Container>
        )}

        {timelineYear && (
          <Container maxWidth={2} mx="auto">
            <Text
              mt={[3, 3, 4]}
              color={whiteText ? "white" : "blue.2"}
              fontSize={[5, 38]}
              fontFamily="boldItalic"
            >
              {timelineYear}
            </Text>
          </Container>
        )}

        <Container maxWidth={2} mx="auto" flexWrap="wrap">
          {!fullMedia && hasImage && (
            <ImageWrap
              mt={[-35, -35, -65]}
              height={[239, 386]}
              display={["block", "block", "none"]}
            >
              <LazyImage src={image.url} alt={image.alt} singleRender={false} />
            </ImageWrap>
          )}
          <Box width={[1, 1, 4 / 10]} pr={[0, 0, 4]}>
            <S_Heading
              as="h1"
              mt={timelineYear ? [3, 3, 4] : 4}
              color={whiteText ? "white" : "blue.2"}
              fontSize={[5, 40]}
              fontFamily="medium"
              letterSpacing={1}
              lineHeight={0.83}
            >
              {title}
            </S_Heading>

            {hasExcerpt && (
              <Text
                my={4}
                color={whiteText ? "white" : "black"}
                fontSize={[3, 3, 4]}
                letterSpacing={0.12}
                lineHeight={1.25}
              >
                {excerpt}
              </Text>
            )}

            {tags && tags.length > 0 && (
              <Box mb={3}>
                <Tags tags={tags} />
              </Box>
            )}

            <Text
              color={whiteText ? "white" : "blue.1"}
              fontSize={1}
              fontFamily="medium"
              letterSpacing={0.12}
              lineHeight={1.4285714286}
            >
              {(details || calendarEvent) && (
                <Box mt={3}>
                  {details && details}
                  {calendarEvent && !pastEvent && (
                    <CalendarWrapper>
                      <AddToCalendar
                        event={calendarEvent}
                        buttonLabel="Add to Calendar"
                      />
                    </CalendarWrapper>
                  )}
                </Box>
              )}
              {(location || address || directionsLink) && (
                <Box mt={3}>
                  {location && <Text>{location}</Text>}
                  {address && <Text>{address}</Text>}
                  {directionsLink && (
                    <S_DynamicNextLink
                      className="underline"
                      link={directionsLink}
                    >
                      Get Directions
                    </S_DynamicNextLink>
                  )}
                </Box>
              )}
              {rsvp && rsvp.url && (
                <Box mt={3}>
                  <Button text="Event Page" link={rsvpLink} variant="blue" />
                </Box>
              )}
              <Box mt={3}>
                <SocialShare title={title} whiteText={whiteText} />
              </Box>
            </Text>
            {sidebarRelated && sidebarRelated.length > 0 && (
              <SidebarRelatedWrap
                mt={4}
                borderTop={`1px ${theme.colors.blue[0]} solid`}
                display={["none", "none", "block"]}
              >
                <RelatedEvents events={sidebarRelated} />
              </SidebarRelatedWrap>
            )}
          </Box>
          <Box
            width={[1, 1, 6 / 10]}
            color={whiteText ? "white" : "black"}
            mt={halfImage ? [3, 3, -65] : [4]}
          >
            {!fullMedia && hasImage && (
              <ImageWrap
                mb={[3, 5]}
                height={[239, 386]}
                display={["none", "none", "block"]}
              >
                <LazyImage
                  src={image.url}
                  alt={image.alt}
                  singleRender={false}
                />
              </ImageWrap>
            )}
            {description && description.length > 0 && (
              <Box>
                <WYSIWYG
                  content={description}
                  handleDangerously={handleDangerously}
                />
              </Box>
            )}
          </Box>
          {sidebarRelated && sidebarRelated.length > 0 && (
            <SidebarRelatedWrap
              mt={4}
              borderTop={`1px ${
                whiteText ? "white" : theme.colors.blue[0]
              } solid`}
              display={["block", "block", "none"]}
            >
              <RelatedEvents events={sidebarRelated} />
            </SidebarRelatedWrap>
          )}
        </Container>
      </Box>
      {image_carousel && image_carousel.length > 0 && (
        <Carousel
          slides={image_carousel.map(image => {
            return { image: image.image_carousel_item };
          })}
        />
      )}
      {hasRelated && (
        <Box
          py={[4, 4, 4, 6]}
          px={[3, 3, 3, 4]}
          bg={whiteText ? "transparent" : "white"}
        >
          <ModuleGrid {...related} />
        </Box>
      )}
    </Box>
  );
};

const BreadcrumbsWrap = styled(Box)`
  ${maxWidth}
  box-sizing: content-box;
`;

const Container = styled(Flex)`
  ${maxWidth}
`;

const EmbedChild = styled(DetailEmbed)`
  ${space}
`;

const ImageWrap = styled(Box)`
  ${height}
  ${display}

  img {
    object-fit: cover;
    object-position: center;
    width: 100%;
    height: 100%;
  }
`;

const S_Heading = styled(Heading)`
  text-transform: uppercase;
`;

const CalendarWrapper = styled(Box)`
  .react-add-to-calendar__wrapper a,
  a:hover {
    cursor: pointer;
    text-decoration: underline;
  }

  .react-add-to-calendar__dropdown {
    margin-left: ${themeGet("space.2")}px;
  }
`;

const SidebarRelatedWrap = styled(Box)`
  ${borderTop}
  ${display}
`;

const S_DynamicNextLink = styled(DynamicNextLink)`
  text-decoration: underline;
`;

export { DetailLayout };
